import { ChoiceGroup } from '@consta/uikit/ChoiceGroup'
import type { BaseEntity } from '@core/core/types'
import { nanoid } from '@reduxjs/toolkit'
import type { DragEvent, FC } from 'react'
import { useCallback, useRef, useState } from 'react'

import { layersMenuChoiceItems, MAX_SELECT_ITEM_LENGTH } from '../constants'
import type { AddedSelectItemType, NavBarChoiceItemsType } from '../types'
import { LayersMenuActions } from './LayersMenuActions'
import { LayersMenuReductionLevel } from './LayersMenuReductionLevel'
import { SelectLayerItem } from './SelectLayerItem'
import type { LayersMenuProps } from './types'
import css from './VelocitySimulationRightMenu.css'

const pickEntityType = (entityType?: string): { key: string; id: string } | undefined => {
  switch (entityType) {
    case 'map':
      return { key: 'map', id: 'mapId' }
    case 'marker':
      return { key: 'marker', id: 'markerId' }
    default:
      return
  }
}

export const LayersMenu: FC<LayersMenuProps> = ({
  choiceValue,
  setChoiceValue,
  addedSelectItemList,
  updateSelectItemList,
  formData,
  setFormData,
  modelResults,
  calculationStatus,
}) => {
  const [isLevelCollapsed, setIsLevelCollapsed] = useState(false)

  const dragItem = useRef<number>(0)
  const dragOverItem = useRef<number>(0)

  const dragStart = useCallback(
    (_: DragEvent, position: number) => (dragItem.current = position),
    [dragItem],
  )
  const dragEnter = useCallback(
    (_: DragEvent, position: number) => (dragOverItem.current = position),
    [dragOverItem],
  )

  const drop = useCallback(() => {
    const copyListItems = [...addedSelectItemList]
    const dragItemContent = copyListItems[dragItem.current]
    copyListItems.splice(dragItem.current, 1)
    copyListItems.splice(dragOverItem.current, 0, dragItemContent)
    dragItem.current = -1
    dragOverItem.current = -1
    updateSelectItemList(copyListItems)
  }, [addedSelectItemList, dragItem, dragOverItem, updateSelectItemList])

  const createSelectItem = () => {
    updateSelectItemList(objectList => [
      ...objectList,
      { id: nanoid(), marker: { value: null }, map: { value: null }, isCollapsed: false },
    ])
  }

  const addSelectItemValue =
    (id: AddedSelectItemType['id']) => (value?: BaseEntity, color?: string) => {
      if (!value) return

      const currentEntity = pickEntityType(value.entityType)
      if (!currentEntity) return

      updateSelectItemList(selectedLayersList =>
        selectedLayersList.map(layer =>
          layer.id === id
            ? {
                ...layer,
                [currentEntity.key]: { value, color },
              }
            : layer,
        ),
      )
      setFormData(prev => ({
        ...prev,
        source: { [currentEntity.id]: value.entityId },
      }))
    }

  const deleteSelectItem = (id: AddedSelectItemType['id']) => () => {
    updateSelectItemList(objectList => objectList.filter(object => object.id !== id))
  }

  const clearSelectItemValue = (id: AddedSelectItemType['id']) => (type: 'map' | 'marker') => {
    const currentEntity = pickEntityType(type)
    if (currentEntity) {
      updateSelectItemList(selectedLayersList =>
        selectedLayersList.map(layer =>
          layer.id === id
            ? {
                ...layer,
                [currentEntity.key]: { value: null, color: undefined },
              }
            : layer,
        ),
      )
      setFormData(prev => ({
        ...prev,
        source: {
          [currentEntity.id]: null,
        },
      }))
    }
  }

  const removeAllLayers = () => {
    updateSelectItemList([])
  }

  const onCollapseItem = (layerId: string) => () => {
    updateSelectItemList(selectedLayersList =>
      selectedLayersList.map(layer =>
        layer.id === layerId
          ? {
              ...layer,
              isCollapsed: true,
            }
          : layer,
      ),
    )
  }

  const onExpandItem = (layerId: string) => () => {
    updateSelectItemList(selectedLayersList =>
      selectedLayersList.map(layer =>
        layer.id === layerId
          ? {
              ...layer,
              isCollapsed: false,
            }
          : layer,
      ),
    )
  }

  const onNavigate = (value: NavBarChoiceItemsType) => {
    setChoiceValue(prevState => ({ ...prevState, layers: value }))
  }

  return (
    <div className={css.wrapper}>
      <ChoiceGroup
        size="xs"
        view="ghost"
        value={choiceValue.layers}
        items={layersMenuChoiceItems}
        getItemLabel={({ name }) => name}
        getItemDisabled={({ disabled }) => disabled}
        name="sortGroup"
        className={css.rightMenuChoiceGroup}
        onChange={onNavigate}
      />
      <LayersMenuReductionLevel
        isLevelCollapsed={isLevelCollapsed}
        setIsLevelCollapsed={setIsLevelCollapsed}
        setReductionLevel={setFormData}
        reductionLevel={formData.reductionLevel}
      />
      <LayersMenuActions
        addedSelectItemList={addedSelectItemList}
        createSelectItem={createSelectItem}
        removeAllLayers={removeAllLayers}
        updateSelectItemList={updateSelectItemList}
      />
      {addedSelectItemList.map(
        (item, index) =>
          index !== MAX_SELECT_ITEM_LENGTH && (
            <SelectLayerItem
              key={item.id}
              index={index}
              dragStart={dragStart}
              dragEnter={dragEnter}
              drop={drop}
              layerInfo={item}
              deleteSelectItem={deleteSelectItem(item.id)}
              clearSelectItemValue={clearSelectItemValue(item.id)}
              addSelectItem={addSelectItemValue(item.id)}
              itemResults={modelResults?.find(res => res?.key === item.id)}
              calculationStatus={calculationStatus}
              onCollapseItem={onCollapseItem(item.id)}
              onExpandItem={onExpandItem(item.id)}
            />
          ),
      )}
    </div>
  )
}
